import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, ChevronRight } from "lucide-react";
import { API } from "@/config";

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filterEra, setFilterEra] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [coachEras, setCoachEras] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [erasRes, seasonsRes] = await Promise.all([
          axios.get(`${API}/coach-eras`),
          axios.get(`${API}/seasons`)
        ]);
        setCoachEras(erasRes.data);
        setSeasons(seasonsRes.data);
      } catch (err) { console.error('Failed to load filter data:', err); }
    };
    fetchFilterData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only event listener

  const handleSearch = async (value, era, year) => {
    setQuery(value);
    const q = value || '';
    
    if (q.length < 2 && !era && !year) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setShowResults(true);
    try {
      const params = new URLSearchParams();
      if (q.length >= 2) params.append('q', q);
      if (era) params.append('coach_era_id', era);
      if (year) params.append('season_year', year);
      params.append('limit', '20');
      
      const res = await axios.get(`${API}/search?${params}`);
      setResults(res.data.videos || res.data.results || res.data);
      setTotalResults(res.data.total || res.data.length);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSeasons = filterEra 
    ? seasons.filter(s => s.coach_era_id === filterEra)
    : seasons;

  const handleFilterChange = (era, year) => {
    setFilterEra(era);
    setFilterYear(year);
    handleSearch(query, era, year);
  };

  return (
    <div className="search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search games, opponents, years..."
          value={query}
          onChange={(e) => handleSearch(e.target.value, filterEra, filterYear)}
          onFocus={() => (query.length >= 2 || filterEra || filterYear) && setShowResults(true)}
          className="search-input"
          data-testid="search-input"
        />
        <button 
          type="button" 
          className={`search-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="search-filter-btn"
        >
          <Calendar size={16} />
          Filters
        </button>
      </div>
      {showFilters && (
        <div className="search-filters">
          <select 
            value={filterEra} 
            onChange={(e) => handleFilterChange(e.target.value, filterYear)}
            className="search-filter-select"
            data-testid="filter-era"
          >
            <option value="">All Coaches</option>
            {coachEras.map(era => (
              <option key={era.id} value={era.id}>{era.coach_name} ({era.years})</option>
            ))}
          </select>
          <select 
            value={filterYear} 
            onChange={(e) => handleFilterChange(filterEra, e.target.value)}
            className="search-filter-select"
            data-testid="filter-year"
          >
            <option value="">All Seasons</option>
            {filteredSeasons.map(s => (
              <option key={s.id} value={s.year}>{s.year} - {s.title}</option>
            ))}
          </select>
          {(filterEra || filterYear) && (
            <button 
              className="search-filter-clear" 
              onClick={() => { setFilterEra(''); setFilterYear(''); setResults([]); setShowResults(false); }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}
      {showResults && (
        <div className="search-results">
          {loading ? (
            <div className="search-loading">Searching...</div>
          ) : results.length === 0 ? (
            <div className="search-no-results">No games found{query ? ` for "${query}"` : ''}</div>
          ) : (
            <>
              <div className="search-results-count">{totalResults} result{totalResults !== 1 ? 's' : ''} found</div>
              {results.map((video) => (
                <Link 
                  key={video.id} 
                  to={`/watch/${video.id}`} 
                  className="search-result-item"
                  onClick={() => setShowResults(false)}
                >
                  <div className="search-result-info">
                    <h4>{video.title}</h4>
                    <p>{video.date} {video.season_year && `\u2022 ${video.season_year}`} {video.opponent && `\u2022 vs. ${video.opponent}`}</p>
                  </div>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
